import { useEffect, useRef, useState } from "react";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP } from "../global_variables";
import "./ResearchInquiryModal.css";

const RECIPIENT_EMAIL = "icarevillagetech@gmail.com";

const AREA_OPTIONS = [
  "Aging at home",
  "Community-based support",
  "Digital health and technology",
  "Service and workforce innovation",
  "Student project",
  "Community collaboration",
  "Other",
];

const emptyForm = {
  name: "",
  organization: "",
  email: "",
  area: "",
  description: "",
};

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const buildMailto = (data) => {
  const subject = encodeURIComponent(
    `Research & Innovation Inquiry — ${data.area || "General"}`
  );
  const body = encodeURIComponent(
    [
      `Name: ${data.name}`,
      `Organization: ${data.organization || "—"}`,
      `Email: ${data.email}`,
      `Area of interest: ${data.area}`,
      "",
      "Idea description:",
      data.description,
    ].join("\n")
  );
  return `mailto:${RECIPIENT_EMAIL}?subject=${subject}&body=${body}`;
};

const ResearchInquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle");
  const firstFieldRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setErrors({});
    setStatus("idle");
    setSubmitting(false);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = setTimeout(() => firstFieldRef.current?.focus(), 60);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(timer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const isDirty = () =>
    Object.values(form).some((v) => String(v).trim().length > 0);

  const handleClose = () => {
    if (submitting) return;
    if (status === "idle" && isDirty()) {
      const ok = window.confirm(
        "Discard your unsent inquiry? Your entries will be lost."
      );
      if (!ok) return;
    }
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please share your name.";
    if (!form.email.trim()) next.email = "Email is required.";
    else if (!isEmail(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (!form.area) next.area = "Please choose an area of interest.";
    if (!form.description.trim())
      next.description = "Please add a brief description of your idea.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      const firstBad = Object.keys(errors)[0] || "name";
      dialogRef.current?.querySelector(`[name="${firstBad}"]`)?.focus();
      return;
    }
    setSubmitting(true);
    setStatus("idle");

    const payload = {
      recipient: RECIPIENT_EMAIL,
      subject: `Research & Innovation Inquiry — ${form.area}`,
      name: form.name.trim(),
      organization: form.organization.trim(),
      email: form.email.trim(),
      area: form.area,
      description: form.description.trim(),
      source: "research-innovation-page",
    };

    try {
      const result = await axiosReqConfig(
        `${API_IP}/api/research-inquiry`,
        "post",
        payload
      );
      setStatus(result?.ok ? "success" : "error");
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const openMailFallback = () => {
    window.location.href = buildMailto(form);
  };

  if (!open) return null;

  return (
    <div
      className="RIM-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="RIM-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="RIM-title"
        aria-describedby="RIM-desc"
        ref={dialogRef}
      >
        <button
          type="button"
          className="RIM-close"
          onClick={handleClose}
          aria-label="Close inquiry form"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 id="RIM-title" className="RIM-title">
          Discuss a Research or Innovation Idea
        </h2>
        <p id="RIM-desc" className="RIM-lede">
          Share a preliminary idea and our team will follow up. Please
          don&rsquo;t include health information, client information, research
          data or participant consent.
        </p>

        {status === "success" ? (
          <div className="RIM-success" role="status" aria-live="polite">
            <div className="RIM-success-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="34" height="34">
                <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M7 12.5l3.5 3.5L17 9" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="RIM-success-title">Thank you — we&rsquo;ll be in touch.</h3>
            <p className="RIM-success-copy">
              Your note has been sent to our research team.
            </p>
            <div className="RIM-actions">
              <button
                type="button"
                className="RIM-btn RIM-btn-primary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form className="RIM-form" onSubmit={handleSubmit} noValidate>
            <div className="RIM-field">
              <label htmlFor="RIM-name">
                Name <span className="RIM-required" aria-hidden="true">*</span>
              </label>
              <input
                ref={firstFieldRef}
                id="RIM-name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={handleChange}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "RIM-err-name" : undefined}
                required
              />
              {errors.name && (
                <p id="RIM-err-name" className="RIM-error" role="alert">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="RIM-field">
              <label htmlFor="RIM-org">
                Organization or institution{" "}
                <span className="RIM-optional">(optional)</span>
              </label>
              <input
                id="RIM-org"
                name="organization"
                type="text"
                autoComplete="organization"
                value={form.organization}
                onChange={handleChange}
              />
            </div>

            <div className="RIM-field">
              <label htmlFor="RIM-email">
                Email address{" "}
                <span className="RIM-required" aria-hidden="true">*</span>
              </label>
              <input
                id="RIM-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "RIM-err-email" : undefined}
                required
              />
              {errors.email && (
                <p id="RIM-err-email" className="RIM-error" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="RIM-field">
              <label htmlFor="RIM-area">
                Area of interest{" "}
                <span className="RIM-required" aria-hidden="true">*</span>
              </label>
              <div className="RIM-select-wrap">
                <select
                  id="RIM-area"
                  name="area"
                  value={form.area}
                  onChange={handleChange}
                  aria-invalid={!!errors.area}
                  aria-describedby={errors.area ? "RIM-err-area" : undefined}
                  required
                >
                  <option value="" disabled>
                    Select an area…
                  </option>
                  {AREA_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <svg
                  className="RIM-select-caret"
                  viewBox="0 0 20 20"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <polyline
                    points="4,7 10,13 16,7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {errors.area && (
                <p id="RIM-err-area" className="RIM-error" role="alert">
                  {errors.area}
                </p>
              )}
            </div>

            <div className="RIM-field">
              <label htmlFor="RIM-description">
                Brief description of the proposed idea{" "}
                <span className="RIM-required" aria-hidden="true">*</span>
              </label>
              <textarea
                id="RIM-description"
                name="description"
                rows={4}
                value={form.description}
                onChange={handleChange}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? "RIM-err-description" : undefined
                }
                required
              />
              {errors.description && (
                <p id="RIM-err-description" className="RIM-error" role="alert">
                  {errors.description}
                </p>
              )}
            </div>

            {status === "error" && (
              <div className="RIM-notice" role="alert">
                We couldn&rsquo;t submit your inquiry.{" "}
                <button
                  type="button"
                  className="RIM-link"
                  onClick={openMailFallback}
                >
                  Send it from your email app instead
                </button>
                .
              </div>
            )}

            <div className="RIM-actions">
              <button
                type="button"
                className="RIM-btn RIM-btn-ghost"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="RIM-btn RIM-btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="RIM-spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  "Send Inquiry"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResearchInquiryModal;
